import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useCallback, useEffect, useRef } from "react";
import { ModeToggle } from "@/components/mode-toggle";
import { LanguageToggle } from "@/components/language-toggle";
import { Button } from "@/components/ui/button";
import { CreditDisplay } from "@/components/ui/credit-display";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Settings,
  Database,
  Home,
  BookOpen,
  LayoutDashboard,
  Briefcase,
  GraduationCap,
  User,
  Brain,
  Flower,
  LogIn,
  LogOut,
  CreditCard,
  UserCircle,
  Award,
  MessageSquare,
  Network,
  Menu as MenuIcon,
  X as CloseIcon,
  ChevronRight,
  Gamepad2,
  Download,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getAllCategoryInfo } from "@/data/course-categories";
import { ContentType, Category } from "@/admin/utils/categoryService";
import { CATALOG_LAYOUT_TYPES } from "@/data/catalog-layouts";
import {
  User as UserType,
  getCurrentUser,
  logout,
} from "@/services/authService";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import MobileMenu from "@/components/ui/mobile-menu";

// Thông tin về các tông phái
const schoolInfo = {
  theravada: {
    name: "Phật giáo Nguyên thủy (Theravada)",
    color: "amber",
  },
  mahayana: {
    name: "Phật giáo Đại thừa (Mahayana)",
    color: "blue",
  },
  vajrayana: {
    name: "Phật giáo Kim cương thừa (Vajrayana)",
    color: "purple",
  },
  zen: {
    name: "Thiền tông (Zen)",
    color: "green",
  },
  "pure-land": {
    name: "Tịnh Độ tông (Pure Land)",
    color: "red",
  },
  "vietnamese-buddhism": {
    name: "Phật giáo Việt Nam",
    color: "orange",
  },
};

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoryByLayout, setCategoryByLayout] = useState<
    Record<string, Category | undefined>
  >({});
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Ref cho mobile menu
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuOverlayRef = useRef<HTMLDivElement>(null);

  // Lấy thông tin user hiện tại
  useEffect(() => {
    let isMounted = true;

    const fetchCurrentUser = async () => {
      try {
        setIsLoadingUser(true);
        const user = await getCurrentUser();
        if (isMounted) {
          setCurrentUser(user);
        }
      } catch (error) {
        console.error("Lỗi khi lấy thông tin user:", error);
        if (isMounted) {
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingUser(false);
        }
      }
    };

    fetchCurrentUser();

    // Cleanup function để tránh memory leak và update state sau khi component unmount
    return () => {
      isMounted = false;
    };
  }, []); // Giữ mảng dependencies rỗng

  // Lấy danh mục từ database khi component được mount
  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        console.log("Fetching categories from database...");
        const categoriesData = await getAllCategoryInfo();

        if (isMounted) {
          console.log("Categories fetched:", categoriesData);
          setCategories(categoriesData);

          // Tạo mapping từ layout_type đến category
          const layoutMapping: Record<string, Category | undefined> = {};
          categoriesData.forEach((category) => {
            if (category.layout_type) {
              layoutMapping[category.layout_type] = category;
            }
          });
          setCategoryByLayout(layoutMapping);
          console.log("Category by layout mapping:", layoutMapping);
        }
      } catch (error) {
        console.error("Lỗi khi lấy danh mục khóa học:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCategories();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  // Lọc danh mục theo content_type
  const courseCategories = categories.filter(
    (category) =>
      // Lọc theo content_type
      category.content_type === ContentType.COURSE ||
      category.content_type === ContentType.ALL ||
      !category.content_type,
  );

  // Hàm lấy icon cho danh mục
  const getCategoryIcon = (categorySlug: string) => {
    switch (categorySlug) {
      case "thien":
      case "thien-dinh":
      case "meditation":
        return <Brain className="mr-2 h-4 w-4" />;
      case "phat-hoc":
      case "buddhism":
      case "buddhist-practices":
        return <BookOpen className="mr-2 h-4 w-4" />;
      case "gia-dinh":
      case "giao-duc-gia-dinh":
      case "family":
      case "family-education":
        return <User className="mr-2 h-4 w-4" />;
      case "triet-hoc-phat-giao":
      case "buddhist-philosophy":
        return <Flower className="mr-2 h-4 w-4" />;
      default:
        return <GraduationCap className="mr-2 h-4 w-4" />;
    }
  };

  // Xử lý chuyển trang với xử lý đặc biệt cho trang courses
  const handleNavigation = useCallback(
    (path: string, setOpen: React.Dispatch<React.SetStateAction<boolean>>) => {
      setOpen(false); // Đóng dropdown trước

      // Kiểm tra nếu đang ở trang courses và sắp điều hướng đến một category khác
      const isCurrentlyCourses = location.pathname.startsWith("/courses");
      const isNavigatingToCourses = path.startsWith("/courses");

      // Sử dụng setTimeout để đảm bảo dropdown đã đóng trước khi chuyển trang
      setTimeout(() => {
        // Nếu đang ở trang courses và chuyển đến một đường dẫn courses khác,
        // thì chúng ta force reload bằng cách thêm timestamp vào URL
        if (
          isCurrentlyCourses &&
          isNavigatingToCourses &&
          location.pathname !== path
        ) {
          // Thêm timestamp vào URL để đảm bảo React Router luôn re-render
          const timestamp = Date.now();
          navigate(`${path}?t=${timestamp}`);
        } else {
          navigate(path);
        }
      }, 10);
    },
    [navigate, location.pathname],
  );

  // Lấy đường dẫn category dựa trên layout_type
  const getCategoryPathByLayout = (layoutType: CATALOG_LAYOUT_TYPES) => {
    const category = categoryByLayout[layoutType];
    if (category) {
      return `/courses/category/${category.slug || category.id}`;
    }

    // Nếu không tìm thấy category với layout_type tương ứng, chuyển về trang courses
    return "/courses";
  };

  // Lấy tên hiển thị cho category dựa trên layout_type
  const getCategoryNameByLayout = (layoutType: CATALOG_LAYOUT_TYPES) => {
    const category = categoryByLayout[layoutType];
    if (category) {
      return category.name;
    }

    // Nếu không tìm thấy category với layout_type tương ứng, hiển thị tên mặc định
    return "Khóa học";
  };

  // Xử lý đăng xuất
  const handleLogout = async () => {
    try {
      // Gọi hàm logout với scope 'local'
      const success = await logout("local");
      if (success) {
        setCurrentUser(null);
        navigate("/");
      }
    } catch (error) {
      console.error("Lỗi khi đăng xuất:", error);
      // Ngay cả khi có lỗi, vẫn đăng xuất ở phía client
      setCurrentUser(null);
      navigate("/");
    }
  };

  // Lấy chữ cái đầu của tên user để hiển thị trong avatar
  const getUserInitials = () => {
    if (!currentUser || !currentUser.full_name) return "?";

    const nameParts = currentUser.full_name.split(" ");
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();

    // Lấy chữ cái đầu của tên và họ
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  // Function để đóng/mở mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);

    // Toggle classes trên overlay và menu
    if (mobileMenuRef.current && mobileMenuOverlayRef.current) {
      if (!mobileMenuOpen) {
        mobileMenuRef.current.classList.add("open");
        mobileMenuOverlayRef.current.classList.add("open");
        document.body.style.overflow = "hidden"; // Khóa scroll khi menu mở
      } else {
        mobileMenuRef.current.classList.remove("open");
        mobileMenuOverlayRef.current.classList.remove("open");
        document.body.style.overflow = ""; // Khôi phục scroll
      }
    }
  };

  // Đóng menu khi click vào overlay
  const closeMobileMenu = () => {
    if (mobileMenuRef.current && mobileMenuOverlayRef.current) {
      mobileMenuRef.current.classList.remove("open");
      mobileMenuOverlayRef.current.classList.remove("open");
      setMobileMenuOpen(false);
      document.body.style.overflow = ""; // Khôi phục scroll
    }
  };

  // Kiểm tra thiết bị di động đơn giản
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Kiểm tra path hiện tại để highlight menu item
  const isActivePath = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b bg-background">
        <div className="container flex h-16 items-center">
          <div className="mr-2 md:mr-4 flex items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img
                src="https://giacngo.org/logo.png"
                alt="Buddha University"
                className="h-8 md:h-12"
              />
              <span className="font-bold text-sm md:text-lg">Giác Ngộ</span>
            </Link>
          </div>

          {/* Mobile Menu Button - Visible only on mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden ml-auto"
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? "Đóng menu" : "Mở menu"}
          >
            {mobileMenuOpen ? (
              <CloseIcon className="h-5 w-5" />
            ) : (
              <MenuIcon className="h-5 w-5" />
            )}
          </Button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-1 items-center space-x-4">
            {/* Sơ đồ Phật học */}
            <Link
              to="/diagrams"
              className={`transition-colors hover:text-foreground/80 ${
                location.pathname.includes("/diagrams")
                  ? "text-foreground"
                  : "text-foreground/60"
              }`}
            >
              <Network className="h-4 w-4 mr-1 inline-block" />
              Sơ đồ
            </Link>

            {/* Chat với AI */}
            <Link
              to="/chat"
              className={`transition-colors hover:text-foreground/80 ${
                location.pathname.includes("/chat") ||
                location.pathname.includes("/ai-chat")
                  ? "text-foreground"
                  : "text-foreground/60"
              }`}
            >
              <MessageSquare className="h-4 w-4 mr-1 inline-block" />
              Chat AI
            </Link>

            {/* Minigames - Menu mới */}
            <Link
              to="/minigames"
              className={`transition-colors hover:text-foreground/80 ${
                location.pathname.includes("/minigames")
                  ? "text-foreground"
                  : "text-foreground/60"
              }`}
            >
              <Gamepad2 className="h-4 w-4 mr-1 inline-block" />
              Trò chơi
            </Link>

            {/* Phật Học - Menu ngoài */}
            <Link
              to="/courses/category/phat-hoc"
              className={`transition-colors hover:text-foreground/80 ${
                location.pathname.includes("/courses/category/phat-hoc")
                  ? "text-foreground"
                  : "text-foreground/60"
              }`}
            >
              <BookOpen className="h-4 w-4 mr-1 inline-block" />
              Phật Học
            </Link>

            {/* Giáo dục gia đình - Menu ngoài */}
            <Link
              to="/courses/category/giao-duc-gia-dinh"
              className={`transition-colors hover:text-foreground/80 ${
                location.pathname.includes(
                  "/courses/category/giao-duc-gia-dinh",
                )
                  ? "text-foreground"
                  : "text-foreground/60"
              }`}
            >
              <User className="h-4 w-4 mr-1 inline-block" />
              Gia Đình
            </Link>

            {/* Công Việc - Menu mới */}
            <Link
              to="/courses/category/cong-viec"
              className={`transition-colors hover:text-foreground/80 ${
                location.pathname.includes("/courses/category/cong-viec")
                  ? "text-foreground"
                  : "text-foreground/60"
              }`}
            >
              <Briefcase className="h-4 w-4 mr-1 inline-block" />
              Công Việc
            </Link>

            {/* Course Categories Dropdown */}
            <DropdownMenu open={coursesOpen} onOpenChange={setCoursesOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="link"
                  className="p-0 text-sm font-medium transition-colors hover:text-primary h-auto"
                >
                  <div className="flex items-center gap-1">
                    <GraduationCap className="h-4 w-4" />
                    <span>{t("courses", { ns: "common" })}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem
                  onClick={() => handleNavigation("/courses", setCoursesOpen)}
                  className="cursor-pointer"
                >
                  <GraduationCap className="mr-2 h-4 w-4" />
                  <span>{t("allCourses", { ns: "courses" })}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                {isLoading ? (
                  <DropdownMenuItem disabled>
                    <span>{t('loading', {ns: 'common'})}</span>
                  </DropdownMenuItem>
                ) : courseCategories && courseCategories.length > 0 ? (
                  // Render danh mục từ database (đã lọc theo content_type)
                  // Loại bỏ "giao-duc-gia-dinh" và "phat-hoc" khỏi danh sách
                  [...courseCategories]
                    .filter(
                      (category) =>
                        category.slug !== "giao-duc-gia-dinh" &&
                        category.slug !== "phat-hoc",
                    )
                    .map((category) => (
                      <DropdownMenuItem
                        key={category.id}
                        onClick={() =>
                          handleNavigation(
                            `/courses/category/${category.slug || category.id}`,
                            setCoursesOpen,
                          )
                        }
                        className="cursor-pointer"
                      >
                        {getCategoryIcon(category.slug || category.id)}
                        <span>{category.name}</span>
                      </DropdownMenuItem>
                    ))
                ) : (
                  // Fallback khi không có danh mục
                  <DropdownMenuItem disabled>
                    <span>{t("courses.noCourses")}</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop Right Menu */}
          <div className="ml-auto hidden md:flex items-center space-x-1">
            {/* PWA Install Button - only visible on mobile devices */}
            {isMobileDevice && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/install-app")}
                className="mr-2 hidden md:flex"
              >
                <Download className="h-4 w-4 mr-1" />
                <span className="hidden lg:inline-block">{t("installApp", "Cài đặt ứng dụng")}</span>
              </Button>
            )}

            {/* Language toggle */}
            <div className="mr-1">
              <LanguageToggle />
            </div>

            {/* Theme toggle */}
            <div className="mr-1">
              <ModeToggle />
            </div>

            {isLoadingUser ? null : !currentUser ? (
              <Button
                size="sm"
                onClick={() => navigate("/login")}
                className="flex items-center gap-1.5"
              >
                <LogIn className="h-4 w-4" />
                <span>{t("main.login", { ns: "nav" })}</span>
              </Button>
            ) : (
              /* Đã gộp phần User Menu và Settings vào một menu */
              <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 h-9"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarImage
                        src={currentUser.avatar}
                        alt={currentUser.full_name}
                      />
                      <AvatarFallback>{getUserInitials()}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-sm hidden sm:inline-block">
                      {currentUser.full_name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel>{currentUser.full_name}</DropdownMenuLabel>

                  {/* Hiển thị credits trong menu */}
                  <div className="px-2 py-1.5">
                    <CreditDisplay />
                  </div>

                  {/* Hiển thị tông phái trong menu */}
                  {currentUser.buddhist_school && (
                    <div className="px-2 py-1.5 flex items-center gap-2">
                      <Flower
                        className={`h-4 w-4 text-${schoolInfo[currentUser.buddhist_school]?.color || "gray"}-500`}
                      />
                      <span className="text-sm text-muted-foreground truncate">
                        {schoolInfo[currentUser.buddhist_school]?.name ||
                          currentUser.buddhist_school}
                      </span>
                    </div>
                  )}

                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    onClick={() =>
                      handleNavigation("/learning-dashboard", setUserMenuOpen)
                    }
                    className="cursor-pointer"
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    <span>{t("user.learningDashboard", { ns: "nav" })}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      handleNavigation("/achievements", setUserMenuOpen)
                    }
                    className="cursor-pointer"
                  >
                    <Award className="mr-2 h-4 w-4" />
                    <span>{t("user.achievements", { ns: "nav" })}</span>
                  </DropdownMenuItem>

                  {!currentUser.buddhist_school && (
                    <DropdownMenuItem
                      onClick={() =>
                        handleNavigation(
                          "/buddhist-school/quiz",
                          setUserMenuOpen,
                        )
                      }
                      className="cursor-pointer"
                    >
                      <Flower className="mr-2 h-4 w-4" />
                      <span>Chọn tông phái của bạn</span>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    onClick={() => handleNavigation("/chat", setUserMenuOpen)}
                    className="cursor-pointer"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    <span>{t("user.aiChat", { ns: "nav" })}</span>
                  </DropdownMenuItem>
                  {!currentUser.is_premium && (
                    <DropdownMenuItem
                      onClick={() =>
                        handleNavigation("/subscription", setUserMenuOpen)
                      }
                      className="cursor-pointer"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      <span>{t("user.upgradeToPremium", { ns: "nav" })}</span>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator />

                  {/* Phần settings và ngôn ngữ */}
                  <DropdownMenuLabel>
                    {t("main.settings", { ns: "nav" })}
                  </DropdownMenuLabel>

                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t("main.darkMode", { ns: "nav" })}
                    </span>
                    <ModeToggle />
                  </div>

                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t("main.language", { ns: "nav" })}
                    </span>
                    <LanguageToggle />
                  </div>

                  <DropdownMenuItem
                    onClick={() =>
                      handleNavigation("/admin/dashboard", setUserMenuOpen)
                    }
                    className="cursor-pointer"
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    <span>{t("main.admin", { ns: "nav" })}</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  {/* Logout */}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t("user.logout", { ns: "nav" })}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Hiển thị ModeToggle riêng khi chưa đăng nhập */}
            {!currentUser && <ModeToggle />}
          </div>

          {/* Mobile Right Menu */}
          <div className="flex md:hidden items-center space-x-1">
            {isLoadingUser ? null : !currentUser ? (
              <>
                <ModeToggle />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="flex items-center gap-1.5"
                >
                  <LogIn className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-9 px-2">
                    <Avatar className="h-7 w-7">
                      <AvatarImage
                        src={currentUser.avatar}
                        alt={currentUser.full_name}
                      />
                      <AvatarFallback>{getUserInitials()}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarImage
                        src={currentUser.avatar}
                        alt={currentUser.full_name}
                      />
                      <AvatarFallback>{getUserInitials()}</AvatarFallback>
                    </Avatar>
                    <span>{currentUser.full_name}</span>
                  </DropdownMenuLabel>

                  {/* Hiển thị credits trong menu */}
                  <div className="px-2 py-1.5">
                    <CreditDisplay />
                  </div>

                  <DropdownMenuSeparator />

                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t("main.darkMode", { ns: "nav" })}
                    </span>
                    <ModeToggle />
                  </div>

                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t("main.language", { ns: "nav" })}
                    </span>
                    <LanguageToggle />
                  </div>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    onClick={() => navigate("/profile")}
                    className="cursor-pointer"
                  >
                    <UserCircle className="mr-2 h-4 w-4" />
                    <span>{t("user.profile", { ns: "nav" })}</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => navigate("/learning-dashboard")}
                    className="cursor-pointer"
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    <span>{t("user.learningDashboard", { ns: "nav" })}</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t("user.logout", { ns: "nav" })}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        ref={mobileMenuOverlayRef}
        className="mobile-menu-overlay"
        onClick={closeMobileMenu}
      ></div>

      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileMenuOpen} setIsOpen={setMobileMenuOpen} />
    </>
  );
}